import { useEffect, useRef } from "react";
import { useCreditCatalog } from "@/hooks/useCreditCatalog";
import { readCreditOffer } from "@/utils/creditOffer";
import { trackEventWithJourney } from "@/utils/analytics";
import {
  clearCreditPurchaseAfterAuth,
  creditPurchaseAfterAuthOffer,
  CREDIT_RESUME_ID_PARAM,
  hasCreditPurchaseAfterAuth,
  hasPendingCreditPurchaseAfterAuth,
  resumeCreditPurchaseAfterAuth,
} from "@/utils/creditAuthIntentPersistence";

const RESTORATION_MISMATCH_GRACE_MS = 15_000;

/**
 * Opens the selected pack after Google or existing-account authentication.
 * Waiting for the restored design keeps purchaseCredits from replacing the
 * anonymous save point with an empty create state before Stripe navigation.
 */
export function usePendingCreditPurchaseResume({
  authLoading,
  hasAccount,
  restorationPending = false,
  frontImagePath,
  resumeKey,
  onResume,
  onIntentUnavailable,
}: {
  authLoading: boolean;
  hasAccount: boolean;
  restorationPending?: boolean;
  frontImagePath: string | null;
  resumeKey: string | null;
  onResume: (
    credits: number,
    price: number,
    stripePriceId: string
  ) => boolean | void | Promise<boolean | void>;
  onIntentUnavailable?: (credits?: number) => void;
}) {
  const catalog = useCreditCatalog();
  const CREDIT_PACKS = catalog.packs;
  const resumeStartedRef = useRef(false);

  useEffect(() => {
    if (
      resumeStartedRef.current ||
      authLoading ||
      catalog.loading ||
      restorationPending ||
      !hasAccount ||
      typeof window === "undefined"
    ) {
      return;
    }

    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.get("credit_intent") !== "1") return;
    const intentId = currentUrl.searchParams.get(CREDIT_RESUME_ID_PARAM);

    const requestedCredits = Number(currentUrl.searchParams.get("credit_pack"));
    const selectedPack = CREDIT_PACKS.find(
      pack => pack.credits === requestedCredits && Boolean(pack.stripePriceId)
    );

    const clearVisibleIntent = () => {
      const latestUrl = new URL(window.location.href);
      latestUrl.searchParams.delete("credit_intent");
      latestUrl.searchParams.delete(CREDIT_RESUME_ID_PARAM);
      latestUrl.searchParams.delete("credit_pack");
      latestUrl.searchParams.delete("credit_gate");
      window.history.replaceState(
        {},
        "",
        latestUrl.pathname + latestUrl.search + latestUrl.hash
      );
    };

    const failResume = (reason: string) => {
      resumeStartedRef.current = true;
      if (intentId) clearCreditPurchaseAfterAuth(intentId);
      clearVisibleIntent();
      trackEventWithJourney("credit_checkout_resume_failed", {
        reason,
        credits: selectedPack?.credits,
      });
      onIntentUnavailable?.(selectedPack?.credits);
    };

    if (!selectedPack) {
      failResume("pack_unavailable");
      return;
    }

    if (!intentId) {
      failResume("intent_missing");
      return;
    }

    // Buying a first preview has no image to restore. An exact restored chat
    // or pending message identifies that design just as an image does. The
    // local intent below must still match; a URL alone never starts checkout.
    const canResumeWithoutImage =
      resumeKey?.startsWith("message:") || resumeKey?.startsWith("session:");
    if (!resumeKey || (!frontImagePath && !canResumeWithoutImage)) {
      if (!hasPendingCreditPurchaseAfterAuth(intentId, selectedPack.credits)) {
        failResume("intent_unavailable");
        return;
      }

      const missingRestoreTimer = window.setTimeout(() => {
        failResume("restoration_timeout");
      }, RESTORATION_MISMATCH_GRACE_MS);
      return () => window.clearTimeout(missingRestoreTimer);
    }

    if (
      !hasCreditPurchaseAfterAuth(intentId, selectedPack.credits, resumeKey)
    ) {
      if (!hasPendingCreditPurchaseAfterAuth(intentId, selectedPack.credits)) {
        failResume("intent_unavailable");
        return;
      }

      const mismatchTimer = window.setTimeout(() => {
        failResume("restoration_mismatch");
      }, RESTORATION_MISMATCH_GRACE_MS);
      return () => window.clearTimeout(mismatchTimer);
    }

    resumeStartedRef.current = true;
    if (!selectedPack.stripePriceId) {
      failResume("pack_unavailable");
      return;
    }
    const storedToken = creditPurchaseAfterAuthOffer(intentId);
    const storedOffer = readCreditOffer(storedToken);
    if (
      storedToken &&
      (!storedOffer ||
        storedOffer.expiresAt <= Date.now() ||
        storedOffer.credits !== selectedPack.credits)
    ) {
      failResume("price_offer_expired");
      return;
    }
    const stripePriceId = storedToken || selectedPack.stripePriceId;

    void (async () => {
      try {
        const outcome = await resumeCreditPurchaseAfterAuth(
          intentId,
          selectedPack.credits,
          resumeKey,
          () => {
            clearVisibleIntent();
            trackEventWithJourney("credit_checkout_resume_started", {
              credits: selectedPack.credits,
            });
            return onResume(
              selectedPack.credits,
              storedOffer ? storedOffer.amount / 100 : selectedPack.price,
              stripePriceId
            );
          }
        );
        if (outcome === "retry") {
          failResume("checkout_retry");
          return;
        }
        clearVisibleIntent();
        if (outcome === "unavailable") failResume("resume_unavailable");
      } catch {
        failResume("checkout_failed");
      }
    })();
  }, [
    CREDIT_PACKS,
    catalog.loading,
    authLoading,
    hasAccount,
    restorationPending,
    frontImagePath,
    resumeKey,
    onResume,
    onIntentUnavailable,
  ]);
}
